import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";

const number = (value) => Number(value || 0);

const money = (value) =>
  `₹${number(value).toLocaleString("en-IN", {
    maximumFractionDigits: 0,
  })}`;

export default function InventoryValueByCategory() {
  const { accessToken } = useAuth();

  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadBalances = useCallback(async () => {
    if (!accessToken) return;

    try {
      setLoading(true);

      const response = await apiRequest("/inventory/balances", {
        accessToken,
      });

      setBalances(response || []);
    } catch {
      setBalances([]);
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    loadBalances();
  }, [loadBalances]);

  const categories = useMemo(() => {
    const grouped = new Map();

    balances.forEach((balance) => {
      const category =
        balance.variant?.product?.category?.name || "Uncategorised";

      const existing = grouped.get(category) || {
        name: category,
        value: 0,
      };

      existing.value +=
        number(balance.quantityOnHand) * number(balance.averageUnitCost);

      grouped.set(category, existing);
    });

    return [...grouped.values()]
      .sort((first, second) => second.value - first.value)
      .slice(0, 4);
  }, [balances]);

  return (
    <div className="mb-6 rounded-3xl border border-gray-100 bg-white p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">
            Inventory Value By Category
          </h2>

          <p className="mt-1 text-sm text-gray-500">
            Based on weighted average inventory cost.
          </p>
        </div>
      </div>

      <div className="grid gap-5 md:grid-cols-4">
        {loading ? (
          <p className="text-sm text-gray-500">Loading category value…</p>
        ) : categories.length === 0 ? (
          <p className="text-sm text-gray-500">
            Add opening stock to see inventory value.
          </p>
        ) : (
          categories.map((category) => (
            <div key={category.name} className="rounded-2xl bg-gray-50 p-5">
              <p className="truncate text-sm text-gray-500">
                {category.name}
              </p>

              <h3 className="mt-2 text-2xl font-bold">
                {money(category.value)}
              </h3>
            </div>
          ))
        )}
      </div>
    </div>
  );
}